<script setup>
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import Avatar from 'next/avatar/Avatar.vue';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
  assignee: {
    type: Object,
    default: null,
  },
  compact: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const store = useStore();
const isUpdating = ref(false);

const currentUser = computed(() => store.getters.getCurrentUser);
const participants = computed(
  () =>
    store.getters['conversationWatchers/getByConversationId'](
      props.conversationId
    ) || []
);

const assigneeId = computed(() =>
  props.assignee?.id != null ? Number(props.assignee.id) : null
);

const extraParticipants = computed(() => {
  if (assigneeId.value == null) return participants.value;
  return participants.value.filter(
    user => Number(user.id) !== assigneeId.value
  );
});

const maxVisible = computed(() => (props.compact ? 2 : 4));

const visibleParticipants = computed(() =>
  extraParticipants.value.slice(0, maxVisible.value)
);

const overflowCount = computed(
  () => extraParticipants.value.length - visibleParticipants.value.length
);

const isCurrentUser = userId =>
  Number(userId) === Number(currentUser.value?.id);

const hasMeta = computed(
  () => Boolean(props.assignee) || visibleParticipants.value.length > 0
);

const fetchParticipants = () => {
  if (!props.conversationId) return;
  store.dispatch('conversationWatchers/show', {
    conversationId: props.conversationId,
  });
};

watch(
  () => props.conversationId,
  () => fetchParticipants(),
  { immediate: true }
);

const displayName = user =>
  isCurrentUser(user.id) ? t('CONVERSATION_PARTICIPANTS.YOU') : user.name;

/**
 * Remove a person from participants. If they were the assignee, promote the
 * next remaining participant (list order) or leave unassigned.
 */
const removeFromConversation = async user => {
  if (isUpdating.value || !user?.id) return;

  const removedId = Number(user.id);
  const remaining = participants.value.filter(
    participant => Number(participant.id) !== removedId
  );
  const wasAssignee = assigneeId.value === removedId;
  const nextAssignee = wasAssignee ? remaining[0] || null : null;
  const removingSelf = isCurrentUser(removedId);

  isUpdating.value = true;
  let alertMessage = removingSelf
    ? t('CONVERSATION_PARTICIPANTS.LEAVE_SUCCESS')
    : t('CONVERSATION_PARTICIPANTS.REMOVE_SUCCESS', {
        name: user.name,
      });

  try {
    await store.dispatch('conversationWatchers/update', {
      conversationId: props.conversationId,
      userIds: remaining.map(participant => participant.id),
    });

    if (wasAssignee) {
      await store.dispatch('assignAgent', {
        conversationId: props.conversationId,
        agentId: nextAssignee ? nextAssignee.id : null,
      });
      if (nextAssignee) {
        alertMessage = t('CONVERSATION_PARTICIPANTS.REMOVE_ASSIGNEE_PROMOTED', {
          removed: user.name,
          next: displayName(nextAssignee),
        });
      } else {
        alertMessage = t(
          'CONVERSATION_PARTICIPANTS.REMOVE_ASSIGNEE_UNASSIGNED',
          {
            name: user.name,
          }
        );
      }
    }
  } catch (error) {
    alertMessage =
      error?.message || t('CONVERSATION_PARTICIPANTS.API.ERROR_MESSAGE');
  } finally {
    isUpdating.value = false;
    useAlert(alertMessage);
  }
};

// Global `button { @apply … py-1 px-2.5 }` (assets/scss/_buttons.scss) zeroed the
// content box of this size-5 circle (20px − border − px-2.5 ⇒ width 0), so Iconify
// and inline SVG both painted an empty ruby disc. Override padding and use a
// literal × glyph that cannot fail to render.
// Absolute × sits in hover-only end padding: tight default (no gap), expands on
// hover so the ruby disc does not cover assignee/participant label text.
const dismissGlyph = '×';

const removeButtonClass =
  'absolute end-0.5 top-1/2 z-10 -translate-y-1/2 !inline-flex !items-center !justify-center !size-5 !min-w-5 !min-h-5 !p-0 !m-0 !leading-none rounded-full shrink-0 !border-2 border-white/90 bg-n-ruby-9 !text-white opacity-0 scale-90 pointer-events-none transition-all duration-150 group-hover:opacity-100 group-hover:scale-100 group-hover:pointer-events-auto focus-visible:opacity-100 focus-visible:scale-100 focus-visible:pointer-events-auto hover:bg-n-ruby-10 disabled:opacity-40';

const chipClass =
  'group relative inline-flex items-center gap-1 min-w-0 rounded-full ps-0.5 pe-1 py-0.5 hover:pe-6 has-[:focus-visible]:pe-6 hover:bg-n-alpha-2 transition-all duration-150';
</script>

<template>
  <span v-if="hasMeta" class="contents">
    <template v-if="assignee">
      <span>•</span>
      <span
        class="group relative inline-flex items-center gap-1 min-w-0 rounded-full ps-0.5 pe-1 py-0.5 hover:pe-6 has-[:focus-visible]:pe-6 ring-0 hover:bg-n-alpha-2 hover:ring-1 hover:ring-n-ruby-7/50 transition-all duration-150"
        :title="t('CONVERSATION_SIDEBAR.ASSIGNEE_LABEL')"
      >
        <Avatar
          :name="assignee.name"
          :src="assignee.thumbnail"
          :size="16"
          rounded-full
          class="shrink-0"
        />
        <span
          v-if="!compact"
          class="truncate max-w-[5.5rem] text-n-slate-11 group-hover:text-n-slate-12"
        >
          {{ displayName(assignee) }}
        </span>
        <span
          v-if="!compact"
          class="hidden group-hover:inline text-[10px] uppercase tracking-wide text-n-ruby-11 font-medium shrink-0"
        >
          {{ t('CONVERSATION_PARTICIPANTS.ASSIGNEE_BADGE') }}
        </span>
        <button
          type="button"
          :class="removeButtonClass"
          :disabled="isUpdating"
          :aria-label="
            t('CONVERSATION_PARTICIPANTS.REMOVE_PERSON', {
              name: displayName(assignee),
            })
          "
          :title="
            t('CONVERSATION_PARTICIPANTS.REMOVE_ASSIGNEE_HINT', {
              name: displayName(assignee),
            })
          "
          @click.stop="removeFromConversation(assignee)"
        >
          <span
            class="block text-[15px] font-bold leading-none text-white select-none"
            aria-hidden="true"
          >
            {{ dismissGlyph }}
          </span>
        </button>
      </span>
    </template>

    <template v-if="visibleParticipants.length">
      <span>•</span>
      <span
        class="inline-flex items-center gap-1 min-w-0 max-w-[14rem] sm:max-w-[18rem]"
        :title="t('CONVERSATION_PARTICIPANTS.SIDEBAR_TITLE')"
      >
        <span
          v-for="participant in visibleParticipants"
          :key="participant.id"
          :class="chipClass"
        >
          <Avatar
            :name="participant.name"
            :src="participant.thumbnail"
            :size="16"
            rounded-full
            class="shrink-0"
          />
          <span
            v-if="!compact"
            class="truncate max-w-[4.5rem] text-n-slate-11 group-hover:text-n-slate-12"
          >
            {{ displayName(participant) }}
          </span>
          <button
            type="button"
            :class="removeButtonClass"
            :disabled="isUpdating"
            :aria-label="
              isCurrentUser(participant.id)
                ? t('CONVERSATION_PARTICIPANTS.LEAVE')
                : t('CONVERSATION_PARTICIPANTS.REMOVE_PERSON', {
                    name: displayName(participant),
                  })
            "
            :title="
              isCurrentUser(participant.id)
                ? t('CONVERSATION_PARTICIPANTS.LEAVE')
                : t('CONVERSATION_PARTICIPANTS.REMOVE_PERSON', {
                    name: displayName(participant),
                  })
            "
            @click.stop="removeFromConversation(participant)"
          >
            <span
              class="block text-[15px] font-bold leading-none text-white select-none"
              aria-hidden="true"
            >
              {{ dismissGlyph }}
            </span>
          </button>
        </span>
        <span
          v-if="overflowCount > 0"
          class="shrink-0 text-n-slate-10"
          :title="
            t('CONVERSATION_PARTICIPANTS.REMANING_PARTICIPANTS_TEXT', {
              count: overflowCount,
            })
          "
        >
          +{{ overflowCount }}
        </span>
      </span>
    </template>
  </span>
</template>
